import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { generateReferral } from "../api";
import { useI18n, formatPrice } from "../i18n";
import { Copy, Share2, DollarSign, Users, Gift } from "lucide-react";

const Referral = () => {
  const { lang } = useI18n();
  const isPt = lang === "pt";
  const [form, setForm] = useState({ name: "", email: "" });
  const [code, setCode] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const shareUrl = useMemo(() => {
    if (!code) return "";
    return `${window.location.origin}/fleet?ref=${code.code}`;
  }, [code]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setSubmitting(true);
    try {
      const res = await generateReferral({
        name: form.name || undefined,
        email: form.email,
      });
      setCode(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error generating code");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (value) => {
    navigator.clipboard.writeText(value);
    toast.success(isPt ? "Copiado!" : "Copied!");
  };

  const tweetText = encodeURIComponent(
    isPt
      ? `Aluguei um carro na @Movix em Boston por menos da metade do preço dos grandes. Usa meu código ${code?.code || ""} e ganhe $${code?.discount_usd || 20} de desconto: `
      : `I rented a car from Movix in Boston for half the price of the big chains. Use my code ${code?.code || ""} for $${code?.discount_usd || 20} off: `
  );

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20" data-testid="referral-page">
      <p className="text-label text-[#FFB400]">
        <span className="line-accent" />
        {isPt ? "Programa de Indicação" : "Referral Program"}
      </p>
      <h1
        className="mt-4 font-heading text-5xl sm:text-6xl font-bold tracking-tighter text-white max-w-3xl"
        data-testid="referral-title"
      >
        {isPt ? (
          <>Indique um amigo, <span className="text-[#FFB400]">ganhe $20</span>.</>
        ) : (
          <>Refer a friend, <span className="text-[#FFB400]">get $20</span>.</>
        )}
      </h1>
      <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-2xl">
        {isPt
          ? "Compartilhe seu código pessoal. Quando seu amigo alugar pela primeira vez, ele economiza $20 e você ganha $20 de crédito na sua próxima locação. Sem limite — indique quantos quiser."
          : "Share your personal code. When your friend books their first rental, they save $20 and you earn $20 credit toward your next trip. No limit — refer as many as you like."}
      </p>

      {/* Steps */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="referral-steps">
        {[
          {
            icon: Users,
            n: "01",
            t: isPt ? "Gere seu código" : "Generate your code",
            d: isPt ? "Em 10 segundos, sem cadastro." : "10 seconds, no signup.",
          },
          {
            icon: Share2,
            n: "02",
            t: isPt ? "Compartilhe" : "Share it",
            d: isPt
              ? "WhatsApp, Instagram, e-mail — o que rolar."
              : "WhatsApp, Instagram, email — wherever.",
          },
          {
            icon: Gift,
            n: "03",
            t: isPt ? "Ambos ganham $20" : "Both get $20",
            d: isPt
              ? "Aplicado direto no checkout."
              : "Applied automatically at checkout.",
          },
        ].map((s, i) => (
          <div key={i} className="bg-[#0a0a0a] p-8">
            <p className="text-label text-[#FFB400]">{s.n}</p>
            <s.icon size={28} className="text-white mt-4" />
            <h3 className="mt-4 font-heading text-xl font-medium text-white">
              {s.t}
            </h3>
            <p className="mt-2 text-zinc-400 text-sm">{s.d}</p>
          </div>
        ))}
      </div>

      {/* Code generator / display */}
      <div className="mt-16">
        {!code ? (
          <form
            onSubmit={onSubmit}
            className="bg-[#0d0d0d] border border-white/10 p-8 md:p-10"
            data-testid="referral-form"
          >
            <p className="text-label">{isPt ? "Crie seu código" : "Create your code"}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
              <div>
                <label className="text-label block mb-2">
                  {isPt ? "Seu nome" : "Your name"}
                </label>
                <input
                  className="movix-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="referral-name"
                  placeholder={isPt ? "(opcional)" : "(optional)"}
                />
              </div>
              <div>
                <label className="text-label block mb-2">
                  {isPt ? "Seu e-mail" : "Your email"} *
                </label>
                <input
                  type="email"
                  required
                  className="movix-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="referral-email"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full justify-center disabled:opacity-60"
                  data-testid="referral-generate"
                >
                  {submitting
                    ? isPt ? "Gerando…" : "Generating…"
                    : isPt ? "Gerar código" : "Generate code"}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div className="bg-[#0d0d0d] border border-[#FFB400]/40 p-10" data-testid="referral-result">
            <p className="text-label text-[#FFB400] mb-3">
              {isPt ? "Seu código está pronto" : "Your code is ready"}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <span
                className="font-mono-ui text-4xl sm:text-5xl font-bold text-[#FFB400] tracking-widest"
                data-testid="referral-code-value"
              >
                {code.code}
              </span>
              <button
                onClick={() => copy(code.code)}
                className="btn-outline py-3"
                data-testid="referral-copy-code"
              >
                <Copy size={14} /> {isPt ? "Copiar código" : "Copy code"}
              </button>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-label mb-3">
                {isPt ? "Link para compartilhar" : "Shareable link"}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  readOnly
                  value={shareUrl}
                  className="movix-input flex-1 text-sm"
                  data-testid="referral-share-url"
                />
                <button
                  onClick={() => copy(shareUrl)}
                  className="btn-outline py-3"
                  data-testid="referral-copy-link"
                >
                  <Copy size={14} /> {isPt ? "Copiar" : "Copy"}
                </button>
                <a
                  href={`https://twitter.com/intent/tweet?text=${tweetText}${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline py-3"
                  data-testid="referral-share-twitter"
                >
                  X / Twitter
                </a>
                <a
                  href={`https://wa.me/?text=${tweetText}${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary py-3"
                  data-testid="referral-share-whatsapp"
                >
                  WhatsApp
                </a>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              <div className="bg-[#0a0a0a] border border-white/10 p-4">
                <DollarSign size={16} className="text-[#FFB400]" />
                <p className="text-label mt-2">
                  {isPt ? "Desconto p/ amigo" : "Friend's discount"}
                </p>
                <p className="font-heading text-2xl font-bold text-white">
                  {formatPrice(code.discount_usd)}
                </p>
              </div>
              <div className="bg-[#0a0a0a] border border-white/10 p-4">
                <Gift size={16} className="text-[#FFB400]" />
                <p className="text-label mt-2">
                  {isPt ? "Seu crédito" : "Your credit"}
                </p>
                <p className="font-heading text-2xl font-bold text-white">
                  {formatPrice(code.discount_usd)}
                </p>
              </div>
              <div className="bg-[#0a0a0a] border border-white/10 p-4">
                <Users size={16} className="text-[#FFB400]" />
                <p className="text-label mt-2">
                  {isPt ? "Resgates" : "Redemptions"}
                </p>
                <p className="font-heading text-2xl font-bold text-white">
                  {code.redemptions}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Referral;
